import { getAnalytics } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import {
  FacebookAuthProvider,
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BeatLoader } from "react-spinners";
import Swal from "sweetalert2";
import HideIcon from "../../assets/Images/eye-crossed.png";
import ViewIcon from "../../assets/Images/eye-open.png";
import LogoIcon from "../../assets/Images/MiniLogo.png";
import axiosClient from "../../Services/Axios/axios";

const Register = () => {
  const firebaseConfig = {
    apiKey: "AIzaSyDvweZ-u_rORGyDoQ-lfvQ_BYnSkcNUYxk",
    authDomain: "world-ngo.firebaseapp.com",
    projectId: "world-ngo",
    storageBucket: "world-ngo.firebasestorage.app",
    messagingSenderId: "342829559045",
    appId: "1:342829559045:web:2ec8c6b1037e91485f9b6c",
    measurementId: "G-4KDR6QDD24",
  };
  const navigate = useNavigate();
  const location = useLocation();

  const app = initializeApp(firebaseConfig);
  const analytics = getAnalytics(app);
  const auth = getAuth(app);

  const googleProvider = new GoogleAuthProvider();
  const facebookProvider = new FacebookAuthProvider();

  const [captchaText, setCaptchaText] = useState("");
  const [userInput, setUserInput] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [emailDisabled, setEmailDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loader, setLoader] = useState(false);
  const [captchaErrorMessage, setCaptchaErrorMessage] = useState("");
  const [registerInfo, setRegisterInfo] = useState({
    first_name: "",
    email: "",
    password: "",
    company_name: "",
    parent_referral_code: "",
    org: "",
    eid: "",
  });

  const [showPassword, setShowPassword] = useState(false);

  const getEmailDataEmployee = async () => {
    const response = await axiosClient.get(
      `users/${process.env.REACT_APP_API_VERSION}/getEmail?eid=${registerInfo.eid}`
    );

    if (!response.data.error) {
      setRegisterInfo((prev) => ({
        ...prev,
        email: response.data.data,
      }));
    }
  };

  // ---------------------------------------------
  //  GET URL PARAMS (org, eid, referral)
  // ---------------------------------------------
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const referral = params.get("referral");
    const org = params.get("org");
    const eid = params.get("eid");

    if (eid) {
      setEmailDisabled(true);
    }
    setRegisterInfo((prev) => ({
      ...prev,
      parent_referral_code: referral || "",
      org: org || "",
      eid: eid || "",
    }));

    generateCaptcha();
  }, [location]);

  useEffect(() => {
    if (emailDisabled) {
      getEmailDataEmployee();
    }
  }, [emailDisabled]);
  // ---------------------------------------------
  // GENERATE CAPTCHA
  // ---------------------------------------------
  const generateCaptcha = () => {
    const chars =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    const text = Array.from(
      { length: 8 },
      () => chars[Math.floor(Math.random() * chars.length)]
    ).join("");

    setCaptchaText(text);
    setUserInput("");
  };

  // ---------------------------------------------
  // NORMAL REGISTER SUBMIT
  // ---------------------------------------------
  const handleSubmit = async (evt) => {
    evt.preventDefault();
    setLoader(true);

    try {
      if (userInput !== captchaText) {
        setLoader(false);
        return Swal.fire({
          icon: "error",
          title: "Captcha Failed",
          text: "Captcha is not valid.",
        });
      }

      // First Name Validation
      if (!registerInfo.first_name) {
        setLoader(false);
        return Swal.fire({
          icon: "error",
          title: "Invalid Input",
          text: "First name is required.",
        });
      }

      if (!/^[A-Za-z\s]*$/.test(registerInfo.first_name)) {
        setLoader(false);
        return Swal.fire({
          icon: "error",
          title: "Invalid Name",
          text: "Name can only contain letters.",
        });
      }

      // Email Validation
      const emailPattern = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,24}$/;

      if (!registerInfo.email) {
        setLoader(false);
        return Swal.fire({
          icon: "error",
          title: "Invalid Email",
          text: "Email is required.",
        });
      }

      if (!emailPattern.test(registerInfo.email)) {
        setLoader(false);
        return Swal.fire({
          icon: "error",
          title: "Invalid Email",
          text: "Please enter a valid email address.",
        });
      }

      // Password Validation
      const { password } = registerInfo;
      const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
      const hasUpper = /[A-Z]/.test(password);
      const hasLower = /[a-z]/.test(password);
      const hasNumber = /\d/.test(password);
      const validLength = password.length >= 8 && password.length <= 15;

      if (!hasSpecial || !hasUpper || !hasLower || !hasNumber || !validLength) {
        setLoader(false);
        return Swal.fire({
          icon: "error",
          title: "Invalid Password",
          text: "Password must include uppercase, lowercase, number & special character.",
        });
      }

      // SEND DATA (now includes org/eid)
      const data = await axiosClient.post(
        `/users/${process.env.REACT_APP_API_VERSION}/register`,
        registerInfo
      );

      if (!data.data.error) {
        localStorage.setItem("email", registerInfo.email);
        setLoader(false);
        navigate("/verification");
      }
    } catch (error) {
      setLoader(false);
      Swal.fire({
        icon: "error",
        title: "Registration Failed",
        text: error.response?.data?.message || "Please try again.",
      });
    }
  };

  // ---------------------------------------------
  // SOCIAL LOGIN COMMON FUNCTION
  // ---------------------------------------------
  const sendSocialRegister = async (user, signupType) => {
    const userData = {
      email: user.email,
      first_name: user.displayName.split(" ")[0],
      last_name: user.displayName.split(" ").slice(1).join(" "),
      profile_image: user.photoURL,
      signup_type: signupType,
      parent_referral_code: registerInfo.parent_referral_code,

      // Important: Now both params included
      org: registerInfo.org,
      eid: registerInfo.eid,
    };

    const response = await axiosClient.post(
      "/users/social-media-register",
      userData
    );

    if (response.data && !response.data.error) {
      localStorage.setItem("userData", JSON.stringify(response.data.data));

      Swal.fire({
        icon: "success",
        title: "Login Successful",
        showConfirmButton: false,
        timer: 1000,
      }).then(() => navigate("/"));
    } else {
      throw new Error(response.data.message || "Social login failed");
    }
  };

  // ---------------------------------------------
  // GOOGLE LOGIN
  // ---------------------------------------------
  const handleGoogleLogin = async () => {
    setLoading(true);

    try {
      const result = await signInWithPopup(auth, googleProvider);
      await sendSocialRegister(result.user, "2");
    } catch (error) {
      navigate("/register");
      setErrorMessage("Google login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------
  // FACEBOOK LOGIN
  // ---------------------------------------------
  const handleFacebookLogin = async () => {
    setLoading(true);

    try {
      const result = await signInWithPopup(auth, facebookProvider);
      await sendSocialRegister(result.user, "1");
    } catch (error) {
      navigate("/register");
      setErrorMessage("Facebook login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------
  // LOADER SCREEN
  // ---------------------------------------------
  if (loader) {
    return (
      <div className="loader">
        <BeatLoader color="#097C81" />
      </div>
    );
  }
  return (
    <>
      <div className="container-fluid vh-100 d-flex justify-content-center login-container manages_top">
        {loading && (
          <>
            <div className="blur-background"></div>
            <div className="loader" style={{ zIndex: 1000, position: "fixed" }}>
              <BeatLoader color="#097C81" />
            </div>
          </>
        )}
        <div className="row w-100 h-100">
          <div className="col-md-6 d-none d-md-block" id="desk_top_hide">
            {/* <div className="h-100 d-flex flex-column justify-content-center align-items-center">
            <img src={LogoIcon} alt="WorldTrust" className="img-fluid" />
            <h5 className="mt-3">Helping with Donations</h5>
          </div> */}
          </div>
          <div
            className="col-md-6 col-sm-12 d-flex flex-column justify-content-center align-items-center"
            id="cnt_managess_area"
          >
            <div className="card p-4 w-100 d-flex flex-column justify-content-center align-items-center sc_lgo_allss">
              <div className="d-flex justify-content-center   ">
                <img src={LogoIcon} alt="WorldTrust" />
              </div>
              <h3 className="text-center">Welcome to WorldTrust</h3>

              <p className="text-center fw-bolder ">
                Continue using email address
              </p>

              <form
                onSubmit={handleSubmit}
                className="w-50"
                id="width_auto_al_screens"
              >
                <div className="form-group mb-2">
                  <label className="mb-1">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Your Name"
                    value={registerInfo.first_name}
                    maxLength={50}
                    onChange={({ target }) =>
                      setRegisterInfo({
                        ...registerInfo,
                        first_name: target.value,
                      })
                    }
                    required
                  />
                </div>

                {/* {registerInfo.role_type !== "5" && (
                  <>
                    <div className="form-group mb-2">
                      <label className="mb-1">Company Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Your Company Name"
                        value={registerInfo.company_name}
                        maxLength={50}
                        onChange={({ target }) =>
                          setRegisterInfo({
                            ...registerInfo,
                            company_name: target.value,
                          })
                        }
                        required
                      />
                    </div>
                  </>
                )} */}

                <div className="form-group mb-2">
                  <label className="mb-1">Email Address</label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Enter Email Address"
                    value={registerInfo.email}
                    maxLength={50}
                    onChange={({ target }) =>
                      setRegisterInfo({
                        ...registerInfo,
                        email: target.value,
                      })
                    }
                    required
                    disabled={emailDisabled}
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="mb-1">Password</label>
                  <div className="input-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-control"
                      placeholder="Enter password"
                      value={registerInfo.password}
                      maxLength={15}
                      onChange={({ target }) =>
                        setRegisterInfo({
                          ...registerInfo,
                          password: target.value,
                        })
                      }
                      required
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary LoginViewButton"
                      onClick={() => setShowPassword(!showPassword)} // Toggle password visibility
                    >
                      {showPassword ? (
                        <img src={ViewIcon} alt="Hide" /> // Change to your hide icon
                      ) : (
                        <img src={HideIcon} alt="View" /> // Change to your view icon
                      )}
                    </button>
                  </div>
                </div>

                <div className="dc_crip_tionss">
                  <p>Your password must have at least:</p>
                  <ul>
                    <li>Minimum 8 characters</li>
                    <li>1 Uppercase Letter</li>
                    <li>1 Lowercase Letter</li>
                    <li>1 Number</li>
                    <li>1 Symbol</li>
                  </ul>
                </div>

                {/*    <div className="form-group redo_sltt_area" id="rediiobnt">
                  <div className="rdo_boxx">
                    <p>
                      <input
                        type="radio"
                        id="individual"
                        name="role_type"
                        checked={registerInfo.role_type === "5"}
                        value="5"
                        onChange={({ target }) =>
                          setRegisterInfo({
                            ...registerInfo,
                            role_type: target.value,
                          })
                        }
                      />
                      <label for="individual">individual</label>
                    </p>
                    <p>
                      <input
                        name="role_type"
                        checked={registerInfo.role_type === "8"}
                        value="8"
                        type="radio"
                        id="NGO"
                        onChange={({ target }) =>
                          setRegisterInfo({
                            ...registerInfo,
                            role_type: target.value,
                          })
                        }
                      />
                      <label for="NGO">NGO</label>
                    </p>
                    <p>
                      <input
                        type="radio"
                        id="Company"
                        name="role_type"
                        checked={registerInfo.role_type === "9"}
                        value="9"
                        onChange={(e) =>
                          setRegisterInfo({
                            ...registerInfo,
                            role_type: e.target.value,
                          })
                        }
                      />
                      <label for="Company">Company</label>
                    </p> 
                  </div>
                  
                </div>
                */}
                <div className="form-group mb-3 capchass">
                  <label className="mb-1">Enter Captcha</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter password"
                      value={captchaText}
                      disabled
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary LoginViewButton"
                      onClick={() => generateCaptcha()}
                    >
                      <img
                        src="/images/chapta_rdo.png"
                        className="chapta_ico"
                        alt=""
                      />
                    </button>
                  </div>
                </div>

                <div className="form-group mb-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Captcha"
                    value={userInput}
                    maxLength={8}
                    onChange={({ target }) => {
                      setUserInput(target.value);
                      // setCaptchErrorMessage("");
                    }}
                  />
                  {/* {captchErrorMessage && (
                    <div className="alert alert-danger mt-3" role="alert">
                      {captchErrorMessage}
                    </div>
                  )} */}
                </div>
                <div className="d-grid mb-3">
                  <button type="submit" className="btn bg-natural btn-block">
                    Register
                  </button>
                </div>
              </form>

              {errorMessage && (
                <div className="alert alert-danger mt-3" role="alert">
                  {errorMessage}
                </div>
              )}

              <p className="text-center mt-2 mb-1">
                If you're already registered, <Link to="/login">Login</Link>
              </p>

              <div className="text-center">Or continue with</div>

              <div
                className="align-items-center gap-2 mt-3 social_bntt both_partss"
                id="width_auto_al_screens"
              >
                <button
                  className="btn btn-danger social_icnn mb-3"
                  onClick={handleGoogleLogin}
                >
                  <img
                    src="/images/google_ic_n.png"
                    className="set_iconss"
                    alt=""
                  />{" "}
                  Use My Google Account
                </button>
                <button
                  className="btn btn-facebook social_icnn"
                  onClick={handleFacebookLogin}
                >
                  <img
                    src="/images/face_bk_ic_n.png"
                    className="set_iconss"
                    alt=""
                  />{" "}
                  Use My Facebook Account
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
